import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Users, Search, Plus, X, Star, Instagram, Youtube, Linkedin, Twitter,
  Globe, Phone, Mail, MapPin, ChevronRight, Filter, UserCircle, Facebook
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { toast } from 'sonner';
import SidebarLayout from '../components/SidebarLayout';
import { useAuth } from '../context/AuthContext';
import ChangeHistoryButton from '../components/ChangeHistoryButton';

import API from '../utils/api';

const PLATFORMS = ['Instagram', 'Facebook', 'YouTube', 'LinkedIn', 'X (Twitter)', 'Other'];
const TYPES = ['Influencer', 'Model'];
const ENGAGEMENTS = ['Low', 'Medium', 'High'];

const PLATFORM_ICONS = {
  'Instagram': <Instagram size={13} className="text-pink-400" />,
  'Facebook': <Facebook size={13} className="text-blue-500" />,
  'YouTube': <Youtube size={13} className="text-red-400" />,
  'LinkedIn': <Linkedin size={13} className="text-sky-400" />,
  'X (Twitter)': <Twitter size={13} className="text-slate-300" />,
  'Other': <Globe size={13} className="text-slate-400" />,
};

const ENGAGEMENT_COLORS = {
  'Low': 'bg-slate-500/10 text-slate-400',
  'Medium': 'bg-amber-500/10 text-amber-400',
  'High': 'bg-emerald-500/10 text-emerald-400',
};

function formatNum(n) {
  if (!n) return '—';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toString();
}

function formatCurrency(n) {
  if (n == null) return '—';
  return '₹' + n.toLocaleString('en-IN');
}

export default function TalentPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canWrite = isAdmin || user?.effective_permissions?.talent === 'edit';
  const [talents, setTalents] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editTalent, setEditTalent] = useState(null);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [search, setSearch] = useState('');
  const [fCategory, setFCategory] = useState('');
  const [fPlatform, setFPlatform] = useState('');
  const [fType, setFType] = useState('');
  const [fCity, setFCity] = useState('');
  const [fFollowersMin, setFFollowersMin] = useState('');
  const [fFollowersMax, setFFollowersMax] = useState('');
  const [fBudgetMin, setFBudgetMin] = useState('');
  const [fBudgetMax, setFBudgetMax] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (fCategory && fCategory !== 'all') params.category = fCategory;
      if (fPlatform && fPlatform !== 'all') params.platform = fPlatform;
      if (fType && fType !== 'all') params.talent_type = fType;
      if (fCity) params.city = fCity;
      if (fFollowersMin) params.followers_min = parseInt(fFollowersMin);
      if (fFollowersMax) params.followers_max = parseInt(fFollowersMax);
      if (fBudgetMin) params.budget_min = parseFloat(fBudgetMin);
      if (fBudgetMax) params.budget_max = parseFloat(fBudgetMax);

      const [talentRes, statsRes, catRes] = await Promise.all([
        axios.get(`${API}/talent`, { withCredentials: true, params }),
        axios.get(`${API}/talent/stats`, { withCredentials: true }),
        axios.get(`${API}/talent/categories`, { withCredentials: true }),
      ]);
      setTalents(talentRes.data);
      setStats(statsRes.data);
      setCategories(catRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, fCategory, fPlatform, fType, fCity, fFollowersMin, fFollowersMax, fBudgetMin, fBudgetMax]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (talentId) => {
    try {
      await axios.delete(`${API}/talent/${talentId}`, { withCredentials: true });
      toast.success('Talent deleted');
      setSelectedTalent(null);
      fetchData();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const clearFilters = () => {
    setFCategory(''); setFPlatform(''); setFType(''); setFCity('');
    setFFollowersMin(''); setFFollowersMax(''); setFBudgetMin(''); setFBudgetMax('');
  };

  const hasFilters = fCategory || fPlatform || fType || fCity || fFollowersMin || fFollowersMax || fBudgetMin || fBudgetMax;

  if (loading && !stats) {
    return <SidebarLayout title="Talent" subtitle="Talent Tracker"><div className="flex items-center justify-center py-20"><div className="text-white">Loading...</div></div></SidebarLayout>;
  }

  return (
    <SidebarLayout title="Talent" subtitle="Talent Tracker">
      {/* KPI Cards */}
      <div className="kpi-scroll md:grid-cols-4 gap-4 mb-6" data-testid="talent-kpis">
        <KPI label="Total Talent" value={stats?.total || 0} color="purple" />
        <KPI label="Influencers" value={stats?.influencers || 0} color="cyan" />
        <KPI label="Models" value={stats?.models || 0} color="amber" />
        <KPI label="Categories" value={Object.keys(stats?.by_category || {}).length} color="emerald" />
      </div>

      {/* Summary Split Tables */}
      {stats && (Object.keys(stats.by_category || {}).length > 0 || Object.keys(stats.by_platform || {}).length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {Object.keys(stats.by_category || {}).length > 0 && (
            <div className="glass-card p-4" data-testid="category-split">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Category Split</h3>
              <div className="space-y-1.5">
                {Object.entries(stats.by_category).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
                  <div key={cat} className="flex justify-between text-xs">
                    <span className="text-slate-300">{cat}</span>
                    <span className="text-white font-bold">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {Object.keys(stats.by_platform || {}).length > 0 && (
            <div className="glass-card p-4" data-testid="platform-split">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Platform Split</h3>
              <div className="space-y-1.5">
                {Object.entries(stats.by_platform).sort((a, b) => b[1] - a[1]).map(([plat, count]) => (
                  <div key={plat} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 text-slate-300">{PLATFORM_ICONS[plat]}{plat}</span>
                    <span className="text-white font-bold">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search & Actions */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or platform..."
            className="pl-9 bg-black/40 border-white/10 text-white h-9 text-sm"
            data-testid="talent-search"
          />
        </div>
        <Button
          onClick={() => setShowFilters(!showFilters)}
          variant="outline"
          className={`h-9 text-xs ${hasFilters ? 'bg-indigo-600/20 border-indigo-500/30 text-indigo-400' : 'bg-white/5 border-white/10 text-slate-300'}`}
          data-testid="talent-filter-toggle"
        >
          <Filter size={14} className="mr-1" /> Filters {hasFilters && `(Active)`}
        </Button>
        {hasFilters && (
          <button onClick={clearFilters} className="text-[10px] text-rose-400 hover:text-rose-300" data-testid="clear-filters">Clear All</button>
        )}
        <div className="flex-1" />
        {canWrite && <Button onClick={() => setShowCreate(true)} className="bg-indigo-600 hover:bg-indigo-700 shadow-[0_0_15px_rgba(79,70,229,0.3)] h-9 text-xs" data-testid="add-talent-btn">
          <Plus size={14} className="mr-1" /> Add Talent
        </Button>}
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="glass-card p-4 mb-4 animate-fadeIn" data-testid="filter-panel">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <Select value={fCategory} onValueChange={setFCategory}>
              <SelectTrigger className="bg-black/40 border-white/10 text-slate-300 h-9 text-xs" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[250px] z-[200]">
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fPlatform} onValueChange={setFPlatform}>
              <SelectTrigger className="bg-black/40 border-white/10 text-slate-300 h-9 text-xs" data-testid="filter-platform"><SelectValue placeholder="Platform" /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                <SelectItem value="all">All Platforms</SelectItem>
                {PLATFORMS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fType} onValueChange={setFType}>
              <SelectTrigger className="bg-black/40 border-white/10 text-slate-300 h-9 text-xs" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                <SelectItem value="all">All Types</SelectItem>
                {TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input value={fCity} onChange={(e) => setFCity(e.target.value)} placeholder="City" className="bg-black/40 border-white/10 text-white h-9 text-xs" data-testid="filter-city" />
            <div className="flex gap-1.5">
              <Input value={fFollowersMin} onChange={(e) => setFFollowersMin(e.target.value)} placeholder="Followers Min" className="bg-black/40 border-white/10 text-white h-9 text-xs" data-testid="filter-followers-min" />
              <Input value={fFollowersMax} onChange={(e) => setFFollowersMax(e.target.value)} placeholder="Max" className="bg-black/40 border-white/10 text-white h-9 text-xs" data-testid="filter-followers-max" />
            </div>
            <div className="flex gap-1.5">
              <Input value={fBudgetMin} onChange={(e) => setFBudgetMin(e.target.value)} placeholder="Budget Min" className="bg-black/40 border-white/10 text-white h-9 text-xs" data-testid="filter-budget-min" />
              <Input value={fBudgetMax} onChange={(e) => setFBudgetMax(e.target.value)} placeholder="Max" className="bg-black/40 border-white/10 text-white h-9 text-xs" data-testid="filter-budget-max" />
            </div>
          </div>
        </div>
      )}

      {/* Talent Table */}
      <div className="glass-card overflow-hidden" data-testid="talent-table">
        <div className="overflow-x-auto mobile-cards">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Platform</th>
              <th>Category</th>
              <th>Followers</th>
              <th>Engagement</th>
              <th>City</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {talents.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-slate-500">No talent found</td></tr>
            ) : talents.map(t => (
              <tr key={t.talent_id} className="cursor-pointer" onClick={() => setSelectedTalent(t)} data-testid={`talent-row-${t.talent_id}`}>
                <td>
                  <div className="flex items-center gap-2">
                    <UserCircle size={20} className="text-indigo-400 shrink-0" />
                    <span className="text-white text-sm font-medium">{t.name}</span>
                  </div>
                </td>
                <td><span className={`text-[10px] px-2 py-0.5 rounded border ${t.type === 'Influencer' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 'bg-pink-500/10 text-pink-400 border-pink-500/20'}`}>{t.type}</span></td>
                <td><span className="flex items-center gap-1 text-xs">{PLATFORM_ICONS[t.dominant_platform]}{t.dominant_platform || '—'}</span></td>
                <td className="text-xs text-slate-400">{(t.categories || []).join(', ') || '—'}</td>
                <td className="text-sm font-mono text-cyan-400 font-bold">{formatNum(t.followers_count)}</td>
                <td>{t.engagement_level ? <span className={`text-[10px] px-2 py-0.5 rounded ${ENGAGEMENT_COLORS[t.engagement_level]}`}>{t.engagement_level}</span> : <span className="text-slate-600 text-xs">—</span>}</td>
                <td className="text-xs text-slate-400">{t.city || '—'}</td>
                <td>
                  <div className="flex items-center gap-1">
                    {isAdmin && <ChangeHistoryButton resource="talent" recordId={t.talent_id} />}
                    <ChevronRight size={14} className="text-slate-600" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Dialogs */}
      {(showCreate || editTalent) && (
        <TalentFormDialog
          talent={editTalent}
          categories={categories}
          onClose={() => { setShowCreate(false); setEditTalent(null); }}
          onSaved={() => { setShowCreate(false); setEditTalent(null); fetchData(); }}
        />
      )}
      {selectedTalent && (
        <TalentDetailSheet
          talent={selectedTalent}
          onClose={() => setSelectedTalent(null)}
          onEdit={canWrite ? () => { setEditTalent(selectedTalent); setSelectedTalent(null); } : undefined}
          onDelete={canWrite ? () => handleDelete(selectedTalent.talent_id) : undefined}
        />
      )}
    </SidebarLayout>
  );
}

function KPI({ label, value, color }) {
  const borders = { purple: 'border-purple-500/20', cyan: 'border-cyan-500/20', emerald: 'border-emerald-500/20', amber: 'border-amber-500/20' };
  return (
    <div className={`kpi-card border-l-4 ${borders[color]}`}>
      <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">{label}</span>
      <div className="font-heading text-3xl font-black tracking-tight text-white mt-1">{value}</div>
    </div>
  );
}

// ========== FORM DIALOG ==========
function TalentFormDialog({ talent, categories, onClose, onSaved }) {
  const isEdit = !!talent;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [localCategories, setLocalCategories] = useState(categories);

  const [form, setForm] = useState({
    name: talent?.name || '',
    type: talent?.type || '',
    dominant_platform: talent?.dominant_platform || '',
    profile_links: talent?.profile_links || {},
    categories: talent?.categories || [],
    followers_count: talent?.followers_count || '',
    engagement_level: talent?.engagement_level || '',
    price_per_reel: talent?.price_per_reel ?? '',
    price_per_post: talent?.price_per_post ?? '',
    price_per_story: talent?.price_per_story ?? '',
    price_per_shoot: talent?.price_per_shoot ?? '',
    phone: talent?.phone || '',
    email: talent?.email || '',
    manager_contact: talent?.manager_contact || '',
    city: talent?.city || '',
    notes: talent?.notes || '',
    past_collaborations: talent?.past_collaborations || [],
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setLink = (platform, url) => setForm(p => ({ ...p, profile_links: { ...p.profile_links, [platform]: url } }));

  const toggleCategory = (cat) => {
    setForm(p => ({
      ...p,
      categories: p.categories.includes(cat) ? p.categories.filter(c => c !== cat) : [...p.categories, cat],
    }));
  };

  const addCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      await axios.post(`${API}/talent/categories`, { name: newCategory.trim() }, { withCredentials: true });
      setLocalCategories(prev => [...prev, newCategory.trim()].sort());
      setForm(p => ({ ...p, categories: [...p.categories, newCategory.trim()] }));
      setNewCategory('');
      toast.success('Category added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add category');
    }
  };

  const addCollab = () => setForm(p => ({ ...p, past_collaborations: [...p.past_collaborations, { client_name: '', date: '' }] }));
  const updateCollab = (i, field, val) => {
    const updated = [...form.past_collaborations];
    updated[i] = { ...updated[i], [field]: val };
    set('past_collaborations', updated);
  };
  const removeCollab = (i) => set('past_collaborations', form.past_collaborations.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    setError(''); setLoading(true);
    try {
      const payload = {
        ...form,
        followers_count: form.followers_count ? parseInt(form.followers_count) : null,
        price_per_reel: form.price_per_reel !== '' ? parseFloat(form.price_per_reel) : null,
        price_per_post: form.price_per_post !== '' ? parseFloat(form.price_per_post) : null,
        price_per_story: form.price_per_story !== '' ? parseFloat(form.price_per_story) : null,
        price_per_shoot: form.price_per_shoot !== '' ? parseFloat(form.price_per_shoot) : null,
        past_collaborations: form.past_collaborations.filter(c => c.client_name),
      };
      if (!payload.engagement_level) delete payload.engagement_level;
      if (!payload.dominant_platform) delete payload.dominant_platform;

      if (isEdit) {
        await axios.put(`${API}/talent/${talent.talent_id}`, payload, { withCredentials: true });
        toast.success('Talent updated');
      } else {
        await axios.post(`${API}/talent`, payload, { withCredentials: true });
        toast.success('Talent added');
      }
      onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(Array.isArray(d) ? d.map(e => e.msg).join(', ') : d || 'Failed');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-[#0a0a0f] border-white/10 max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-white font-heading text-lg">{isEdit ? 'Edit Talent' : 'Add Talent'}</DialogTitle></DialogHeader>
        {error && <div className="p-2 rounded bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs">{error}</div>}
        <div className="space-y-4 py-2">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Name *</label>
              <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Full name" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="talent-name" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Type *</label>
              <Select value={form.type} onValueChange={(v) => set('type', v)}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="talent-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                  {TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Platform */}
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Dominant Platform</label>
            <Select value={form.dominant_platform} onValueChange={(v) => set('dominant_platform', v)}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="talent-platform"><SelectValue placeholder="Select platform" /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                {PLATFORMS.map(p => <SelectItem key={p} value={p}><span className="flex items-center gap-1.5">{PLATFORM_ICONS[p]}{p}</span></SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Profile Links */}
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Profile Links</label>
            <div className="space-y-2">
              {PLATFORMS.map(p => (
                <div key={p} className="flex items-center gap-2">
                  <span className="w-28 flex items-center gap-1.5 text-xs text-slate-400">{PLATFORM_ICONS[p]}{p}</span>
                  <Input
                    value={form.profile_links[p] || ''}
                    onChange={(e) => setLink(p, e.target.value)}
                    placeholder={`${p} profile URL`}
                    className="bg-black/40 border-white/10 text-white h-8 text-xs flex-1"
                    data-testid={`link-${p.toLowerCase().replace(/[\s()]/g, '')}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Categories */}
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Categories</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {localCategories.map(c => (
                <button
                  key={c}
                  onClick={() => toggleCategory(c)}
                  className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors ${form.categories.includes(c) ? 'bg-indigo-600/20 border-indigo-500/30 text-indigo-400' : 'bg-white/5 border-white/10 text-slate-400 hover:text-white'}`}
                  data-testid={`cat-${c.toLowerCase()}`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="New category..." className="bg-black/40 border-white/10 text-white h-8 text-xs flex-1" data-testid="new-category-input" />
              <Button onClick={addCategory} variant="outline" className="h-8 text-xs bg-white/5 border-white/10 text-slate-300" data-testid="add-category-btn"><Plus size={12} className="mr-1" />Add</Button>
            </div>
          </div>

          {/* Reach */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Followers Count</label>
              <Input type="number" value={form.followers_count} onChange={(e) => set('followers_count', e.target.value)} placeholder="e.g. 150000" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="talent-followers" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Engagement Level</label>
              <Select value={form.engagement_level} onValueChange={(v) => set('engagement_level', v)}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="talent-engagement"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                  {ENGAGEMENTS.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Commercials */}
          <div>
            <label className="text-xs text-slate-500 mb-2 block">Commercials (₹)</label>
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" value={form.price_per_reel} onChange={(e) => set('price_per_reel', e.target.value)} placeholder="Price per Reel" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="price-reel" />
              <Input type="number" value={form.price_per_post} onChange={(e) => set('price_per_post', e.target.value)} placeholder="Price per Post" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="price-post" />
              <Input type="number" value={form.price_per_story} onChange={(e) => set('price_per_story', e.target.value)} placeholder="Price per Story" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="price-story" />
              <Input type="number" value={form.price_per_shoot} onChange={(e) => set('price_per_shoot', e.target.value)} placeholder="Price per Shoot" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="price-shoot" />
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Phone</label>
              <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="Phone" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="talent-phone" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Email</label>
              <Input value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="Email" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="talent-email" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Manager Contact</label>
              <Input value={form.manager_contact} onChange={(e) => set('manager_contact', e.target.value)} placeholder="Manager" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="talent-manager" />
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="text-xs text-slate-500 mb-1 block">City</label>
            <Input value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="e.g. Mumbai" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="talent-city" />
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Notes</label>
            <Input value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Past experience, brand fit, special remarks..." className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="talent-notes" />
          </div>

          {/* Past Collaborations */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-slate-500">Past Collaborations</label>
              <button onClick={addCollab} className="text-[10px] text-indigo-400 hover:text-indigo-300 flex items-center gap-0.5" data-testid="add-collab-btn"><Plus size={10} /> Add</button>
            </div>
            {form.past_collaborations.map((c, i) => (
              <div key={i} className="flex gap-2 mb-2 items-center">
                <Input value={c.client_name} onChange={(e) => updateCollab(i, 'client_name', e.target.value)} placeholder="Client name" className="bg-black/40 border-white/10 text-white h-8 text-xs flex-1" data-testid={`collab-name-${i}`} />
                <input type="date" value={c.date || ''} onChange={(e) => updateCollab(i, 'date', e.target.value)} className="bg-black/40 border border-white/10 text-white text-xs rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500" data-testid={`collab-date-${i}`} />
                <button onClick={() => removeCollab(i)} className="text-rose-400 hover:text-rose-300 p-1"><X size={12} /></button>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-white">Cancel</Button>
          <Button disabled={loading || !form.name || !form.type} onClick={handleSubmit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-talent">{loading ? 'Saving...' : isEdit ? 'Update Talent' : 'Add Talent'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ========== DETAIL SHEET ==========
function TalentDetailSheet({ talent, onClose, onEdit, onDelete }) {
  const t = talent;
  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg bg-[#0a0a0f] border-l border-white/10 overflow-y-auto">
        <SheetHeader className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] px-2 py-0.5 rounded border ${t.type === 'Influencer' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 'bg-pink-500/10 text-pink-400 border-pink-500/20'}`}>{t.type}</span>
            {t.engagement_level && <span className={`text-[10px] px-2 py-0.5 rounded ${ENGAGEMENT_COLORS[t.engagement_level]}`}>{t.engagement_level}</span>}
          </div>
          <SheetTitle className="text-white font-heading text-xl" data-testid="talent-detail-title">{t.name}</SheetTitle>
        </SheetHeader>

        <div className="space-y-4">
          {/* Platform & Reach */}
          <div className="glass-card p-4">
            <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Platform & Reach</h3>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-slate-500">Dominant Platform</span><span className="flex items-center gap-1 text-white">{PLATFORM_ICONS[t.dominant_platform]}{t.dominant_platform || '—'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Followers</span><span className="text-cyan-400 font-bold font-mono">{formatNum(t.followers_count)}</span></div>
              {t.followers_updated_at && <div className="flex justify-between"><span className="text-slate-500">Followers Updated</span><span className="text-slate-400">{new Date(t.followers_updated_at).toLocaleDateString('en-IN')}</span></div>}
              {t.engagement_level && <div className="flex justify-between"><span className="text-slate-500">Engagement</span><span className={`px-2 py-0.5 rounded ${ENGAGEMENT_COLORS[t.engagement_level]}`}>{t.engagement_level}</span></div>}
            </div>
          </div>

          {/* Profile Links */}
          {Object.keys(t.profile_links || {}).some(k => t.profile_links[k]) && (
            <div className="glass-card p-4">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Profile Links</h3>
              <div className="space-y-2">
                {Object.entries(t.profile_links).filter(([, url]) => url).map(([plat, url]) => (
                  <div key={plat} className="flex items-center gap-2 text-xs">
                    {PLATFORM_ICONS[plat]}
                    <a href={url} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 break-all">{url}</a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Categories */}
          {(t.categories || []).length > 0 && (
            <div className="glass-card p-4">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Categories</h3>
              <div className="flex flex-wrap gap-1.5">
                {t.categories.map(c => (
                  <span key={c} className="text-[10px] px-2.5 py-1 rounded-full bg-indigo-600/20 border border-indigo-500/30 text-indigo-400">{c}</span>
                ))}
              </div>
            </div>
          )}

          {/* Commercials */}
          <div className="glass-card p-4">
            <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Commercials</h3>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex justify-between"><span className="text-slate-500">Per Reel</span><span className="text-amber-400 font-bold">{formatCurrency(t.price_per_reel)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Per Post</span><span className="text-amber-400 font-bold">{formatCurrency(t.price_per_post)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Per Story</span><span className="text-amber-400 font-bold">{formatCurrency(t.price_per_story)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Per Shoot</span><span className="text-amber-400 font-bold">{formatCurrency(t.price_per_shoot)}</span></div>
            </div>
          </div>

          {/* Contact */}
          <div className="glass-card p-4">
            <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Contact</h3>
            <div className="space-y-2 text-xs">
              {t.phone && <div className="flex items-center gap-2"><Phone size={12} className="text-slate-500" /><span className="text-white">{t.phone}</span></div>}
              {t.email && <div className="flex items-center gap-2"><Mail size={12} className="text-slate-500" /><span className="text-white">{t.email}</span></div>}
              {t.manager_contact && <div className="flex items-center gap-2"><Users size={12} className="text-slate-500" /><span className="text-slate-300">Manager: {t.manager_contact}</span></div>}
              {t.city && <div className="flex items-center gap-2"><MapPin size={12} className="text-slate-500" /><span className="text-white">{t.city}</span></div>}
            </div>
          </div>

          {/* Notes */}
          {t.notes && (
            <div className="glass-card p-4">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-2">Notes</h3>
              <p className="text-xs text-slate-300">{t.notes}</p>
            </div>
          )}

          {/* Past Collaborations */}
          {(t.past_collaborations || []).length > 0 && (
            <div className="glass-card p-4">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Past Collaborations</h3>
              <div className="space-y-1.5">
                {t.past_collaborations.map((c, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="text-white">{c.client_name}</span>
                    <span className="text-slate-400">{c.date || '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          {(onEdit || onDelete) && (
            <div className="flex gap-2">
              {onEdit && <Button onClick={onEdit} className="flex-1 bg-indigo-600 hover:bg-indigo-700 h-9 text-xs" data-testid="edit-talent-btn">Edit Talent</Button>}
              {onDelete && <Button onClick={onDelete} variant="outline" className="bg-rose-500/10 border-rose-500/20 text-rose-400 h-9 text-xs" data-testid="delete-talent-btn"><X size={14} className="mr-1" /> Delete</Button>}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
